import axios, { AxiosResponse } from 'axios';
import {
  FilterRequest,
  ResourceAnalyticsResponse,
  OrganizationalAnalyticsResponse,
  EmployeeDetailsResponse,
  UtilizationActionableInsightsResponse,
  EmployeeAllocationMonthlyResponse,
  EmployeeAllocationMonthlyRow,
  EmployeeMonthlyInsightsResponse,
  EmployeeMonthlyInsightsSummary,
  DashboardSummaryResponse,
  FilterOptionsResponse,
  ExportRequest,
  PredictiveAnalyticsRequest,
  PredictiveAnalyticsResponse,
  ClientSuccessRequest,
  ClientSuccessResponse,
  OperationalExcellenceRequest,
  OperationalExcellenceResponse,
  DrillDownRequest,
  DrillDownResponse,
  ProjectsDashboardResponse,
  ProjectsAnalyticsResponse,
  ProjectHealthMetrics,
  ProjectSummaryMetrics,
  ProjectHealthScore,
  RegionalAnalytics,
  FundingAnalysis,
  AccountPerformance,
  RevenueByStatus,
  RAGHealthMatrix,
  ProjectDetailsFilterRequest,
  ProjectDetailsResponse,
  ProjectsPortfolioResponse,
  ExecutiveSummaryMilestonesPayload,
  ExecutiveSummaryOutcomesPayload,
  ExecutiveSummaryWeeklyDescriptionPayload,
  ExecutiveSummaryWeeklyBulletListPayload,
  ExecutiveSummaryRaidPayload,
  ExecutiveSummaryKpisPayload,
  PeopleAnalyticsFilterRequest,
  PeopleAnalyticsResponse,
  PeopleCurrentMetrics,
  PeopleWeeklyMovement,
  PeopleAttritionRate,
  PeopleTenureAnalysis,
  PeopleGenderBalance,
  PeopleGeographicDistribution,
  PeopleMonthlyTrends,
  // Squad Structure Types
  SquadStructureFilterRequest,
  SquadStructureResponse,
  SquadDetailResponse,
  // Unallocated Members and Business Units Types
  UnallocatedMembersResponse,
  UnallocatedBusinessUnitsResponse,
  // User Profile Types
  UserProfileResponse,
  // My Insights Types
  MyInsightsResponse,
  // My Files Types
  MyFilesFilterRequest,
  MyFilesResponse,
  UserResourceAnalyticsResponse,
  DefaulterDataResponse,
  ProjectStatusCounts,

} from '../types/api';
import type { Tier1TrendRecord, CompetencyTrendRecord, CompetencyProgressCard } from '../types/driInsights';
import type {
  HrStatsBreakdownRequest,
  HrStatsBreakdownResponse,
  HrStatsPeopleRequest,
  HrStatsPeopleResponse,
  HrStatsBaseRequest,
  HrStatsSummaryResponse,
  HrStatsDimensionValuesRequest,
  HrStatsDimensionValuesResponse,
} from '../types/hrStats';
import type {
  AssetAiQueryRequest,
  AssetAiQueryResponse,
  AssetCreateRequest,
  AssetListResponse,
  AssetResponse,
} from '../types/assets';
import type {
  BillingBudget,
  BillingBudgetCreate,
  BillingBudgetListResponse,
  BillingBusinessUnitOption,
  GcpBudgetUpsertRequest,
  GcpBudgetUpsertResponse,
} from '../types/billing';

// dummy

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const AI_BASE_URL = process.env.REACT_APP_AI_CHATBOT_URL || 'http://localhost:8080';

/** Plain JSON body for AI /query — only `role` + `content` keys per turn (matches strict Pydantic models). */
type AssetAiQueryWireTurn = { role: 'user' | 'assistant'; content: string };

/** Max attachment size for asset AI /query (aligned with story PDF cap). */
const ASSET_AI_QUERY_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

function buildAssetAiQueryPayload(request: AssetAiQueryRequest): {
  user_question: string;
  chat_history?: AssetAiQueryWireTurn[];
} {
  const user_question = String(request.user_question ?? '').trim();
  const raw = request.chat_history ?? [];
  const chat_history: AssetAiQueryWireTurn[] = [];
  for (const t of raw) {
    const role: 'user' | 'assistant' | null =
      t.role === 'assistant' ? 'assistant' : t.role === 'user' ? 'user' : null;
    if (!role) continue;
    const content =
      typeof t.content === 'string' ? t.content.trim() : String(t.content ?? '').trim();
    if (!content) continue;
    chat_history.push({ role, content });
  }
  const payload: { user_question: string; chat_history?: AssetAiQueryWireTurn[] } = {
    user_question,
  };
  if (chat_history.length > 0) {
    payload.chat_history = chat_history;
  }
  return payload;
}

function normalizeAssetAiQueryBody(request: AssetAiQueryRequest): string {
  return JSON.stringify(buildAssetAiQueryPayload(request));
}
// User Feedback Types
export interface UserFeedbackRequest {
  user_email: string;
  user_name: string;
  category: string;
  subject: string;
  feedback: string;
}

export interface UserFeedbackResponse {
  success: boolean;
  message: string;
  feedback_id?: string;
}

/** Single item when listing all user feedback (admin) */
export interface UserFeedbackItem {
  id: string;
  user_email: string;
  user_name: string;
  category: string;
  subject: string;
  feedback: string;
  submitted_at: string;
  reference_urls?: string[];
  /** Set when feedback has been marked resolved (list or resolve response). */
  resolved_at?: string | null;
}

export interface ResolveUserFeedbackResponse {
  success: boolean;
  resolved_at: string;
}

export interface GetAllUserFeedbackResponse {
  feedbacks: UserFeedbackItem[];
  total_count?: number;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});


// Request interceptor for auth and logging
api.interceptors.request.use(
  (config) => {
    // Add authorization header if token exists
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // API response logging removed for production
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle 401 errors (unauthorized) - clear token and redirect to login
      if (error.response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const dashboardApi = {

  /**
   * Get filter options. Query param `scope` can be: projects, org, time, people, product.
   * If scopes is omitted, all scopes are requested.
   */
  getFilterOptions: async (scopes?: Array<'projects' | 'org' | 'time' | 'people' | 'product'>): Promise<FilterOptionsResponse> => {
    const scopeList = scopes && scopes.length > 0 ? scopes : ['projects', 'org', 'time', 'people', 'product'];
    
    const response = await api.get<FilterOptionsResponse>(`/dashboard/v2/filters?scopes=${scopeList.join(',')}`);
    return response.data;
},

  // Get Sub-BU options based on Business Units
  getSubBusByBusinessUnits: async (businessUnits: string[]): Promise<string[]> => {
    if (!businessUnits || businessUnits.length === 0) {
      return [];
    }
    const response = await api.post('/dashboard/sub-bus', { business_units: businessUnits });
    return response.data;
  },

  // Get Delivery Manager options (value=email, label=name), cascaded off BU + Sub-BU
  getDeliveryManagers: async (
    businessUnits: string[],
    subBus: string[]
  ): Promise<Array<{ name: string; email: string }>> => {
    if (!businessUnits || businessUnits.length === 0) {
      return [];
    }
    const response = await api.post('/dashboard/delivery-managers', {
      business_units: businessUnits,
      sub_bus: subBus ?? [],
    });
    return response.data;
  },

  // Get Squad options ("SBU | Squad"), cascaded off BU + Sub-BU + Delivery Manager
  getSquads: async (
    businessUnits: string[],
    subBus: string[],
    deliveryManagers: string[]
  ): Promise<string[]> => {
    if (!businessUnits || businessUnits.length === 0) {
      return [];
    }
    const response = await api.post('/dashboard/squads', {
      business_units: businessUnits,
      sub_bus: subBus ?? [],
      delivery_managers: deliveryManagers ?? [],
    });
    return response.data;
  },

  // Get Product options based on Business Units (delivery: Postgres; org: BigQuery)
  getProductsByBusinessUnits: async (
    businessUnits: string[],
    scope: 'delivery' | 'org' = 'delivery'
  ): Promise<string[]> => {
    if (scope === 'org') {
      const response = await api.get('/dri-insights/products', { params: { scope: 'org' } });
      return response.data;
    }
    if (!businessUnits || businessUnits.length === 0) {
      return [];
    }
    const response = await api.get('/dri-insights/products', {
      params: { scope: 'delivery', business_units: businessUnits },
    });
    return response.data;
  },

  /** Org-scope filter options from BigQuery employees table (admin only). */
  getDriOrgBusinessUnits: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/dri-insights/org/business-units');
    return response.data;
  },

  getDriOrgSubBus: async (businessUnits: string[]): Promise<string[]> => {
    if (!businessUnits || businessUnits.length === 0) {
      return [];
    }
    const response = await api.get<string[]>('/dri-insights/org/sub-bus', {
      params: { business_units: businessUnits },
    });
    return response.data;
  },


  // Resource Analytics
  getResourceAnalytics: async (filters: FilterRequest): Promise<ResourceAnalyticsResponse> => {
    const filterInfo = {
      start_year: filters.start_year,
      start_month: filters.start_month,
      end_year: filters.end_year,
      end_month: filters.end_month,
      year: filters.year,
      months: filters.months,
      business_units_count: filters.business_units?.length || 0,
      employee_status: filters.employee_status
    };
    // console.log('Frontend API - getResourceAnalytics called with filters:', JSON.stringify(filterInfo, null, 2));
    const response = await api.post('/dashboard/resource-analytics', filters);
    const detailedData = response.data?.detailed_data || [];
    const totalActualInDetailed = detailedData.reduce((sum: number, emp: any) => sum + (emp.actual_hours || 0), 0);
    const responseInfo = {
      total_actual_hours: response.data?.total_actual_hours || 0,
      detailed_data_count: detailedData.length,
      total_actual_in_detailed: totalActualInDetailed,
      total_allocated_hours: response.data?.total_allocated_hours || 0,
      first_5_employees_actual_hours: detailedData.slice(0, 5).map((emp: any) => ({
        name: `${emp.first_name} ${emp.last_name}`,
        actual_hours: emp.actual_hours
      }))
    };
    // console.log('Frontend API - getResourceAnalytics response:', JSON.stringify(responseInfo, null, 2));
    return response.data;
  },

  getMyResourceAnalytics: async (filters: FilterRequest): Promise<ResourceAnalyticsResponse> => {
    const filterInfo = {
      start_year: filters.start_year,
      start_month: filters.start_month,
      end_year: filters.end_year,
      end_month: filters.end_month,
      year: filters.year,
      months: filters.months,
      business_units_count: filters.business_units?.length || 0,
      employee_status: filters.employee_status
    };
    // console.log('Frontend API - getResourceAnalytics called with filters:', JSON.stringify(filterInfo, null, 2));
    const response = await api.post('/my-insights/my-resource-analytics', filters);
    const detailedData = response.data?.detailed_data || [];
    const totalActualInDetailed = detailedData.reduce((sum: number, emp: any) => sum + (emp.actual_hours || 0), 0);
    const responseInfo = {
      total_actual_hours: response.data?.total_actual_hours || 0,
      detailed_data_count: detailedData.length,
      total_actual_in_detailed: totalActualInDetailed,
      total_allocated_hours: response.data?.total_allocated_hours || 0,
      first_5_employees_actual_hours: detailedData.slice(0, 5).map((emp: any) => ({
        name: `${emp.first_name} ${emp.last_name}`,
        actual_hours: emp.actual_hours
      }))
    };
    // console.log('Frontend API - getResourceAnalytics response:', JSON.stringify(responseInfo, null, 2));
    return response.data;
  },

  // Organizational Analytics
  getOrganizationalAnalytics: async (filters: FilterRequest): Promise<OrganizationalAnalyticsResponse> => {
    const response = await api.post('/dashboard/organizational-analytics', filters);
    return response.data;
  },
  getGeneralTasksBreakdown: async (filters: FilterRequest): Promise<OrganizationalAnalyticsResponse> => {
    const response = await api.post('/insights/general-tasks-breakdown', filters);
    return response.data;
  },



  // Dashboard Summary
  getDashboardSummary: async (filters: FilterRequest): Promise<DashboardSummaryResponse> => {
    const response = await api.post('/analytics/summary', filters);
    return response.data;
  },

  // Headcount Trends
  getMonthlyHeadcountTrends: async (filters: FilterRequest): Promise<any[]> => {
    const response = await api.post('/analytics/headcount-trends', filters);
    return response.data;
  },

  // Attrition Trends
  getMonthlyAttritionTrends: async (filters: FilterRequest): Promise<any[]> => {
    const response = await api.post('/analytics/attrition-trends', filters);
    return response.data;
  },

  // Get Total Headcount (all employees, respecting employee_status filter, excluding duration)
  getTotalHeadcount: async (filters: FilterRequest): Promise<{ total_headcount: number }> => {
    // Create filters without duration fields
    const { start_year, start_month, end_year, end_month, year, months, ...filtersWithoutDuration } = filters;
    const response = await api.post('/analytics/headcount/total', filtersWithoutDuration);
    return response.data || { total_headcount: 0 };
  },

  // Get Total Active Headcount (only active employees, ignoring employee_status filter, excluding duration)
  getTotalActiveHeadcount: async (filters: FilterRequest): Promise<{ total_active_headcount: number }> => {
    // Create filters without duration fields and employee_status
    const { start_year, start_month, end_year, end_month, year, months, employee_status, ...filtersWithoutDurationAndStatus } = filters;
    // Force employee_status to ACTIVE
    const response = await api.post('/analytics/headcount/active', { ...filtersWithoutDurationAndStatus, employee_status: 'ACTIVE' });
    return response.data || { total_active_headcount: 0 };
  },
  // Utilization Trends //need to check this below api
  getMonthlyUtilizationTrends: async (filters: FilterRequest): Promise<any[]> => {
    const response = await api.post('/analytics/utilization-trends', filters);
    return response.data;
  },
  getMyMontlyUtilizationTrends: async (filters: FilterRequest): Promise<any[]> => {
    const response = await api.post('/my-insights/my-utilization-trend', filters);
    return response.data;
  },

  // Predictive Analytics
  getPredictiveAnalytics: async (request: PredictiveAnalyticsRequest): Promise<PredictiveAnalyticsResponse> => {
    const response = await api.post('/dashboard/predictive-analytics', request);
    return response.data;
  },

  // Client Success
  getClientSuccess: async (request: ClientSuccessRequest): Promise<ClientSuccessResponse> => {
    const response = await api.post('/dashboard/client-success', request);
    return response.data;
  },

  // Operational Excellence
  getOperationalExcellence: async (request: OperationalExcellenceRequest): Promise<OperationalExcellenceResponse> => {
    const response = await api.post('/dashboard/operational-excellence', request);
    return response.data;
  },

  // Drill Down
  drillDown: async (request: DrillDownRequest): Promise<DrillDownResponse> => {
    const response = await api.post('/dashboard/drill-down', request);
    return response.data;
  },

  // Export Data
  exportData: async (request: ExportRequest): Promise<Blob> => {
    const response = await api.post('/dashboard/export', request, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get Employee Details
  getEmployeeDetails: async (request: FilterRequest): Promise<EmployeeDetailsResponse> => {
    const { page, page_size, ...requestFilters } = request;
    const filterInfo = {
      start_year: requestFilters.start_year,
      start_month: requestFilters.start_month,
      end_year: requestFilters.end_year,
      end_month: requestFilters.end_month,
      year: requestFilters.year,
      months: requestFilters.months,
      business_units_count: requestFilters.business_units?.length || 0,
      employee_status: requestFilters.employee_status
    };
    // console.log('Frontend API - getEmployeeDetails called with filters:', JSON.stringify(filterInfo, null, 2));
    const response = await api.post('/analytics/employee-details', requestFilters);
    const employees = response.data?.employees || [];
    const totalActualHours = employees.reduce((sum: number, emp: any) => sum + (emp.total_actual_hours || 0), 0);
    const responseInfo = {
      employees_count: employees.length,
      total_actual_hours: totalActualHours,
      first_5_employees_actual_hours: employees.slice(0, 5).map((emp: any) => ({
        name: emp.employee_name,
        actual_hours: emp.total_actual_hours
      }))
    };
    // console.log('Frontend API - getEmployeeDetails response:', JSON.stringify(responseInfo, null, 2));
    return response.data;
  },

  getUtilizationActionableInsights: async (
    request: FilterRequest
  ): Promise<UtilizationActionableInsightsResponse> => {
    const response = await api.post('/analytics/utilization-actionable-insights', request);
    return response.data;
  },

  getEmployeeMonthlyInsights: async (request: FilterRequest & { employee_email: string; employee_name?: string }): Promise<EmployeeMonthlyInsightsResponse> => {
    const response = await api.post('/analytics/employee-monthly-insights', request);
    const d = response.data as Partial<EmployeeMonthlyInsightsResponse>;
    const s = (d.summary ?? {}) as Partial<EmployeeMonthlyInsightsSummary>;
    const prof = (d.profile ?? {}) as Partial<EmployeeMonthlyInsightsResponse['profile']>;
    return {
      employee_email: d.employee_email ?? '',
      employee_name: d.employee_name ?? '',
      monthly_series: d.monthly_series ?? [],
      profile: {
        designation: String(prof.designation ?? ''),
        reporting_manager: String(prof.reporting_manager ?? ''),
        squad: String(prof.squad ?? ''),
        squad_role: String(prof.squad_role ?? ''),
        work_mode_profile: String(prof.work_mode_profile ?? ''),
      },
      projects: (d.projects ?? []).map((p) => ({
        project_code: String(p.project_code ?? ''),
        project_name: String(p.project_name ?? ''),
        account: String(p.account ?? ''),
        status: String(p.status ?? ''),
        project_kind: String(p.project_kind ?? ''),
        start_date: p.start_date ?? null,
        end_date: p.end_date ?? null,
        total_allocation_hours: Number(p.total_allocation_hours ?? 0),
        total_revenue: Number(p.total_revenue ?? 0),
        rag_status: String(p.rag_status ?? ''),
      })),
      certifications: d.certifications ?? [],
      expiring_certifications: d.expiring_certifications ?? [],
      competencies_count: d.competencies_count ?? 0,
      competencies: d.competencies ?? [],
      allocation_status: String(d.allocation_status ?? 'Bench'),
      summary: {
        billable_utilization: s.billable_utilization ?? 0,
        internal_utilization: s.internal_utilization ?? 0,
        total_utilization: s.total_utilization ?? 0,
        projects_total: s.projects_total ?? 0,
        projects_active: s.projects_active ?? 0,
        projects_closed: s.projects_closed ?? 0,
        client_projects_active: s.client_projects_active ?? 0,
        client_projects_closed: s.client_projects_closed ?? 0,
        internal_projects_active: s.internal_projects_active ?? 0,
        internal_projects_closed: s.internal_projects_closed ?? 0,
        total_allocated_revenue: s.total_allocated_revenue ?? 0,
        utilization_potential_hours: s.utilization_potential_hours ?? 0,
        billable_numerator_hours: s.billable_numerator_hours ?? 0,
        billable_client_billable_allocated_hours: s.billable_client_billable_allocated_hours ?? 0,
        billable_internal_project_actual_hours: s.billable_internal_project_actual_hours ?? 0,
        internal_numerator_hours: s.internal_numerator_hours ?? 0,
        total_numerator_hours: s.total_numerator_hours ?? 0,
      },
    };
  },

  /**
   * Per-month resource allocation rows for a single employee. Fetched once per (email, Duration)
   * and sliced client-side for the Allocation Breakdown M/Q/Y strip — so changing granularity
   * or the cursor does NOT refetch anything.
   */
  getEmployeeAllocationMonthly: async (
    request: FilterRequest & { employee_email: string; employee_name?: string },
  ): Promise<EmployeeAllocationMonthlyResponse> => {
    const response = await api.post('/analytics/employee-allocation-monthly', request);
    const d = response.data as Partial<EmployeeAllocationMonthlyResponse>;
    const rows: EmployeeAllocationMonthlyRow[] = (d.rows ?? []).map((r) => ({
      year: Number(r.year ?? 0),
      month: String(r.month ?? ''),
      month_num: Number(r.month_num ?? 0),
      project_code: String(r.project_code ?? ''),
      hours: Number(r.hours ?? 0),
      revenue: Number(r.revenue ?? 0),
      status: String(r.status ?? ''),
    }));
    return {
      employee_email: d.employee_email ?? '',
      rows,
    };
  },

  // Get Projects Dashboard (legacy; prefer individual project APIs below)
  getProjectsDashboard: async (filters?: any): Promise<ProjectsDashboardResponse> => {
    const response = await api.post('/projects/projects-dashboard', filters || {});
    return response.data;
  },

  // Projects dashboard – split APIs
  getProjectsHealthMetrics: async (filters?: any): Promise<ProjectHealthMetrics> => {
    const response = await api.post<ProjectHealthMetrics>('/projects/health-metrics', filters || {});
    return response.data;
  },
  getProjectsSummaryMetrics: async (filters?: any): Promise<ProjectSummaryMetrics> => {
    const response = await api.post<ProjectSummaryMetrics>('/projects/summary-metrics', filters || {});
    return response.data;
  },
  getProjectsRegionalAnalytics: async (filters?: any): Promise<RegionalAnalytics[]> => {
    const response = await api.post<RegionalAnalytics[]>('/projects/regional-analytics', filters || {});
    return response.data;
  },
  getProjectsFundingAnalytics: async (filters?: any): Promise<FundingAnalysis[]> => {
    const response = await api.post<FundingAnalysis[]>('/projects/funding-analysis', filters || {});
    return response.data;
  },
  // getProjectsQuarterlyTrends: async (filters?: any): Promise<QuarterlyTrends[]> => {
  //   const response = await api.post<QuarterlyTrends[]>('/projects/quaterly-trends', filters || {});
  //   return response.data;
  // },
  getProjectsAccountPerformance: async (filters?: any): Promise<AccountPerformance[]> => {
    const response = await api.post<AccountPerformance[]>('/projects/account-performance', filters || {});
    return response.data;
  },
  getProjectsProjectMetrics: async (filters?: any): Promise<{ status_counts: ProjectStatusCounts, revenue_by_status: RevenueByStatus[] }> => {
    const response = await api.post<{ status_counts: ProjectStatusCounts, revenue_by_status: RevenueByStatus[] }>('/projects/projects-metrics', filters || {});
    return response.data;
  },
  getProjectsRagHealthMetrics: async (filters?: any): Promise<RAGHealthMatrix[]> => {
    const response = await api.post<RAGHealthMatrix[]>('/projects/rag-health-metrics', filters || {});
    return response.data;
  },
  getProjectsProjectHealthMetrics: async (filters?: any): Promise<ProjectHealthScore> => {
    const response = await api.post<ProjectHealthScore>('/projects/health-metrics', filters || {});
    return response.data;
  },

  getMyDriInsights: async (): Promise<any> => {
    const response = await api.get('/my-insights/dri-insights');
    return response.data;
  },

  // Get Projects Analytics
  getProjectsAnalytics: async (): Promise<ProjectsAnalyticsResponse> => {
    const response = await api.get('/dashboard/projects/analytics');
    return response.data;
  },

  // Get Monthly New Projects
  getMonthlyNewProjects: async (filters?: any): Promise<any[]> => {
    const response = await api.post('/projects/project-trends', filters || {});
    return response.data;
  },

  // Get Project Allocation Details
  getProjectAllocationDetails: async (filters?: any): Promise<any[]> => {
    const response = await api.post('/projects/allocation-details', filters || {});
    return response.data;
  },


  getProjectsPortfolio: async (filters: ProjectDetailsFilterRequest): Promise<ProjectsPortfolioResponse> => {
    const { page, page_size, ...requestFilters } = filters as ProjectDetailsFilterRequest & {
      page?: number;
      page_size?: number;
    };
    const response = await api.post('/projects/projects-portfolio', requestFilters);
    return response.data;
  },

  getExecutiveSummaryMilestones: async (projectCode: string): Promise<ExecutiveSummaryMilestonesPayload> => {
    const response = await api.get('/projects/executive-summary/milestones', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryOutcomes: async (projectCode: string): Promise<ExecutiveSummaryOutcomesPayload> => {
    const response = await api.get('/projects/executive-summary/outcomes', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryWeeklyDescription: async (
    projectCode: string
  ): Promise<ExecutiveSummaryWeeklyDescriptionPayload> => {
    const response = await api.get('/projects/executive-summary/description', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryWeeklyHighlights: async (
    projectCode: string
  ): Promise<ExecutiveSummaryWeeklyBulletListPayload> => {
    const response = await api.get('/projects/executive-summary/highlights', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryWeeklyLowlights: async (
    projectCode: string
  ): Promise<ExecutiveSummaryWeeklyBulletListPayload> => {
    const response = await api.get('/projects/executive-summary/lowlights', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryRaid: async (projectCode: string): Promise<ExecutiveSummaryRaidPayload> => {
    const response = await api.get('/projects/executive-summary/raid', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getExecutiveSummaryKpis: async (projectCode: string): Promise<ExecutiveSummaryKpisPayload> => {
    const response = await api.get('/projects/executive-summary/kpis', {
      params: { project_code: projectCode },
    });
    return response.data;
  },

  getProjectDetails: async (filters: ProjectDetailsFilterRequest): Promise<ProjectDetailsResponse> => {
    const { page, page_size, ...requestFilters } = filters;
    // console.log('🌐 API Service Debug - Sending to backend:', {
    //   url: '/projects/project-details',
    //   filters: requestFilters,
    //   project_owners: requestFilters.project_owners,
    //   filtersStringified: JSON.stringify(requestFilters, null, 2)
    // });
    const response = await api.post('/projects/project-details', requestFilters);
    // console.log('🌐 API Service Debug - Backend response:', {
    //   status: response.status,
    //   dataLength: response.data?.projects?.length || 0,
    //   totalProjects: response.data?.total_projects || 0
    // });
    return response.data;
  },

  // People Analytics APIs
  getPeopleAnalytics: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleAnalyticsResponse> => {
    const response = await api.post('/dashboard/people-analytics', filters || {});
    return response.data;
  },

  getPeopleCurrentMetrics: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleCurrentMetrics> => {
    const response = await api.get('/dashboard/people-analytics/current-metrics', { params: filters });
    return response.data;
  },

  getPeopleWeeklyMovement: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleWeeklyMovement> => {
    const response = await api.get('/dashboard/people-analytics/weekly-movement', { params: filters });
    return response.data;
  },

  getPeopleAttritionRates: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleAttritionRate[]> => {
    const response = await api.get('/dashboard/people-analytics/attrition-rates', { params: filters });
    return response.data;
  },

  getPeopleTenureAnalysis: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleTenureAnalysis[]> => {
    const response = await api.get('/dashboard/people-analytics/tenure-analysis', { params: filters });
    return response.data;
  },

  getPeopleGenderBalance: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleGenderBalance[]> => {
    const response = await api.get('/dashboard/people-analytics/gender-balance', { params: filters });
    return response.data;
  },

  getPeopleGeographicDistribution: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleGeographicDistribution[]> => {
    // FastAPI expects repeated query parameters for arrays: ?business_units=BU1&business_units=BU2
    // Only BU/SBU and employee_status are used for filtering - other fields are returned in data but not filtered
    // console.log('Frontend API - getPeopleGeographicDistribution called with filters:', JSON.stringify(filters, null, 2));

    // Build query string manually to ensure arrays are sent as repeated query parameters
    const queryParams: string[] = [];

    if (filters) {
      // Only include BU/SBU and employee_status - these are the only filters used
      if (filters.business_units && filters.business_units.length > 0) {
        filters.business_units.forEach((bu: string) => {
          queryParams.push(`business_units=${encodeURIComponent(bu)}`);
        });
      }
      if (filters.sub_bus && filters.sub_bus.length > 0) {
        filters.sub_bus.forEach((subBu: string) => {
          queryParams.push(`sub_bus=${encodeURIComponent(subBu)}`);
        });
      }
      // Handle employee_status
      if (filters.employee_status) {
        queryParams.push(`employee_status=${encodeURIComponent(filters.employee_status)}`);
      }
    }

    const queryString = queryParams.join('&');
    const url = `/dashboard/people-analytics/geographic-distribution${queryString ? '?' + queryString : ''}`;

    // console.log('Frontend API - getPeopleGeographicDistribution constructed URL:', url);
    // console.log('Frontend API - Query string:', queryString);

    const response = await api.get(url);

    // console.log('Frontend API - getPeopleGeographicDistribution response count:', response.data?.length || 0);
    return response.data;
  },

  getPeopleMonthlyTrends: async (filters?: PeopleAnalyticsFilterRequest): Promise<PeopleMonthlyTrends[]> => {
    const response = await api.get('/dashboard/people-analytics/monthly-trends', { params: filters });
    return response.data;
  },

  // Debug endpoint for geographic distribution
  debugGeographicDistribution: async (filters?: PeopleAnalyticsFilterRequest): Promise<any> => {
    const queryParams: string[] = [];

    if (filters) {
      if (filters.business_units && filters.business_units.length > 0) {
        filters.business_units.forEach((bu: string) => {
          queryParams.push(`business_units=${encodeURIComponent(bu)}`);
        });
      }
      if (filters.sub_bus && filters.sub_bus.length > 0) {
        filters.sub_bus.forEach((subBu: string) => {
          queryParams.push(`sub_bus=${encodeURIComponent(subBu)}`);
        });
      }
      if (filters.employee_status) {
        queryParams.push(`employee_status=${encodeURIComponent(filters.employee_status)}`);
      }
    }

    const queryString = queryParams.join('&');
    const url = `/dashboard/debug-geographic-distribution${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
  },
  // Squad Structure APIs
  getSquadStructure: async (filters?: SquadStructureFilterRequest): Promise<SquadStructureResponse> => {
    const response = await api.post('/squad/squad-structure', filters || { date_range_months: 12 });
    console.log("data:", response.data);
    return response.data;
  },

  getSquadDetail: async (squadId: string, businessUnit: string, subBu: string, filters?: SquadStructureFilterRequest): Promise<SquadDetailResponse> => {
    const response = await api.post('/squad/squad-detail', filters || { date_range_months: 12 }, {
      params: {
        squad_id: squadId,
        business_unit: businessUnit,
        sub_bu: subBu,
      },
    });
    return response.data;
  },
  getSquadProjects: async (squadId: string, status: string = 'active', dateRangeMonths: number = 12, subBu: string): Promise<any[]> => {
    const response = await api.get('/squad/squad-projects', {
      params: {
        squad_id: squadId,
        status: status,
        sub_bu: subBu,
        date_range_months: dateRangeMonths,
      },
    });
    return response.data;
  },

  getSquadAvailableCapacity: async (squadId: string, businessUnit: string, subBu: string): Promise<any> => {
    const response = await api.get('/squad/squad-available-capacity', {
      params: {
        squad_id: squadId,
        business_unit: businessUnit,
        sub_bu: subBu,
      },
    });
    return response.data;
  },

  // Unallocated Members and Business Units
  getUnallocatedMembers: async (businessUnits?: string[]): Promise<UnallocatedMembersResponse> => {
    const response = await api.post('/squad/unallocated-members', {
      business_units: businessUnits
    });
    return response.data;
  },

  getUserProfile: async (): Promise<UserProfileResponse> => {
    const response = await api.get(`/my-insights/user-profile`);
    return response.data;
  },

  createInternalProjectRequest: async (requestData: {
    // Basic Information
    name: string;
    email: string;
    manager_email: string;
    sbu: string;
    sbu_lead_email: string;

    // Asset Type and Basic Details
    asset_type: string;
    title: string;
    short_description: string;

    // Conditional Fields for Accelerator/IP
    problem_statement_question?: string;
    problem_impact_metrics?: string;
    target_audience_description?: string;
    solution_overview?: string;
    key_features?: string;
    hyperscaler_services?: string;
    known_dependencies?: string;
    diagrams_links?: string;
    gtm_category?: string;
    google_partnership_strategy?: string;
    specialized_skills_enhancement?: string;

    // Common Fields
    value_proposition?: string;
    resource_estimate?: string;

    // Project Timeline
    estimated_duration_weeks?: number | null;
    estimated_start_date?: string | null;
    estimated_end_date?: string | null;

    required_skills?: string;
    known_risks?: string;
    critical_prerequisites?: string;

    // For POC/Project types
    detailed_description?: string;
  }): Promise<any> => {
    const response = await api.post('/approval/internal-project-request', requestData);
    return response.data;
  },

  getApprovalConfig: async (): Promise<{ final_approver_name: string; final_approver_email: string }> => {
    const response = await api.get('/approval/approval-config');
    return response.data;
  },

  getMyProposals: async (): Promise<any> => {
    const response = await api.get('/approval/my-proposals');
    return response.data;
  },

  getAllProposals: async (): Promise<any> => {
    const response = await api.get('/approval/all-proposals');
    return response.data;
  },

  getProposalById: async (proposalId: string): Promise<any> => {
    const response = await api.get(`/approval/proposal/${proposalId}`);
    return response.data;
  },

  updateProposal: async (proposalId: string, proposalData: any): Promise<any> => {
    const response = await api.put(`/approval/proposal/${proposalId}`, proposalData);
    return response.data;
  },

  deleteProposal: async (proposalId: string): Promise<any> => {
    const response = await api.delete(`/approval/proposal/${proposalId}`);
    return response.data;
  },

  // Forms API
  getForms: async (page = 1, pageSize = 25, status?: string): Promise<any> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (status) params.append('status', status);

    const response = await api.get(`/dashboard/forms?${params}`);
    return response.data;
  },

  getForm: async (formId: string): Promise<any> => {
    const response = await api.get(`/dashboard/forms/${formId}`);
    return response.data;
  },

  createForm: async (formData: any): Promise<any> => {
    const response = await api.post('/dashboard/forms', formData);
    return response.data;
  },

  updateForm: async (formId: string, formData: any): Promise<any> => {
    const response = await api.put(`/dashboard/forms/${formId}`, formData);
    return response.data;
  },

  deleteForm: async (formId: string): Promise<any> => {
    const response = await api.delete(`/dashboard/forms/${formId}`);
    return response.data;
  },

  submitForm: async (formId: string, submissionData: any): Promise<any> => {
    const response = await api.post(`/dashboard/forms/${formId}/submit`, submissionData);
    return response.data;
  },

  getFormSubmissions: async (formId: string, page = 1, pageSize = 25): Promise<any> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const response = await api.get(`/dashboard/forms/${formId}/submissions?${params}`);
    return response.data;
  },

  // Approval System APIs
  getPendingApprovals: async (): Promise<any> => {
    const response = await api.get('/approval/pending-approvals');
    return response.data;
  },

  getApprovedByMe: async (): Promise<any> => {
    const response = await api.get('/approval/approved-by-me');
    return response.data;
  },

  approveProposal: async (proposalId: string, remarks: string = ''): Promise<any> => {
    const response = await api.post(`/approval/proposal/${proposalId}/approve`, {
      action_type: 'approve',
      remarks: remarks
    });
    return response.data;
  },

  rejectProposal: async (proposalId: string, remarks: string = ''): Promise<any> => {
    const response = await api.post(`/approval/proposal/${proposalId}/reject`, {
      action_type: 'reject',
      remarks: remarks
    });
    return response.data;
  },

  addRemark: async (proposalId: string, remarks: string): Promise<any> => {
    const response = await api.post(`/approval/proposal/${proposalId}/add-remark`, {
      action_type: 'remark',
      remarks: remarks
    });
    return response.data;
  },

  // Two-Level Approval Workflow APIs
  level1Approve: async (proposalId: string, remarks?: string): Promise<any> => {
    const response = await api.post(`/approval/internal-project-request/${proposalId}/level1-approve`, {
      remarks: remarks || ''
    });
    return response.data;
  },

  level1Reject: async (proposalId: string, rejectionReason: string): Promise<any> => {
    const response = await api.post(
      `/approval/internal-project-request/${proposalId}/level1-reject?rejection_reason=${encodeURIComponent(rejectionReason)}`
    );
    return response.data;
  },

  level2Approve: async (proposalId: string, remarks?: string): Promise<any> => {
    const response = await api.post(`/approval/internal-project-request/${proposalId}/level2-approve`, {
      remarks: remarks || ''
    });
    return response.data;
  },

  level2Reject: async (proposalId: string, rejectionReason: string): Promise<any> => {
    const response = await api.post(
      `/approval/internal-project-request/${proposalId}/level2-reject?rejection_reason=${encodeURIComponent(rejectionReason)}`
    );
    return response.data;
  },

  resubmitProposal: async (proposalId: string, proposalData: any): Promise<any> => {
    const response = await api.put(`/approval/internal-project-request/${proposalId}/resubmit`, proposalData);
    return response.data;
  },

  closeProposal: async (proposalId: string): Promise<any> => {
    const response = await api.put(`/approval/internal-project-request/${proposalId}/close`);
    return response.data;
  },

  // PR Manager APIs
  addPRCode: async (proposalId: string, prCode: string, remarks?: string): Promise<any> => {
    const response = await api.post(`/approval/proposal/${proposalId}/add-pr-code`, {
      pr_code: prCode,
      remarks: remarks || ''
    });
    return response.data;
  },

  getPendingPRCreation: async (): Promise<any> => {
    const response = await api.get('/approval/pending-pr-creation');
    return response.data;
  },

  // My Insights
  getMyInsights: async (filters?: FilterRequest): Promise<MyInsightsResponse> => {
    const response = await api.post('/insights/my-insights', filters || {});
    return response.data;
  },

  // User Resource Analytics
  getUserResourceAnalytics: async (filters?: FilterRequest): Promise<UserResourceAnalyticsResponse> => {
    const response = await api.post('/analytics/resource', filters || {});
    return response.data;
  },

  // Defaulter Data
  getMyDefaulterData: async (): Promise<DefaulterDataResponse> => {
    const response = await api.get('/my-insights/my-defaulter-data');
    return response.data;
  },

  getMyProjectData: async (filters?:FilterRequest): Promise<any> => {
    const response = await api.post('/my-insights/my-projects',filters || {});
    return response.data;
  },

  // DRI Registration Status
  getDriRegistrationStatus: async (): Promise<{ is_registered: boolean; user_email: string }> => {
    const response = await api.get('/insights/dri-registration-status');
    return response.data;
  },

  // My Team endpoints
  getMyTeam: async (filters?: FilterRequest): Promise<MyInsightsResponse> => {
    const response = await api.post('/team-insights/my-team', filters || {});
    return response.data;
  },

  getMyTeamResourceAnalytics: async (filters?: FilterRequest): Promise<UserResourceAnalyticsResponse> => {
    const response = await api.post('/team-insights/my-team-resource-analytics', filters || {});
    return response.data;
  },

  /** Team members from team-insights API (single source: team_emails, managers, team_member_names) */
  getTeamInsightsMyTeamMembers: async (): Promise<{
    team_emails: string[];
    managers: Array<{ email: string; name: string }>;
    team_member_names: string[];
    team_member_details: Array<{ email: string; name: string; status: string }>;
  }> => {
    const response = await api.get('/team-insights/my-team-members');
    return response.data;
  },

  getMyTeamDriDetails: async (filters?: FilterRequest): Promise<any[]> => {
    const response = await api.post('/team-insights/my-team-dri-details', filters || {});
    return response.data;
  },

  getMyTeamSbus: async (): Promise<string[]> => {
    const response = await api.get('/team-insights/my-team-sbus');
    return response.data;
  },

  // DRI Insights
  getDriInsights: async (filters?: FilterRequest): Promise<any> => {
    const response = await api.post('/dri-insights/dri', filters || {} );
    return response.data;
  },

  // My Skill Badges (for My Insights page)
  getMySkillBadges: async (): Promise<{ skill_badges: any[] }> => {
    const response = await api.get('/my-insights/my-skill-badges');
    return response.data;
  },

  // My Certifications (for My Insights page)
  getMyCertifications: async (): Promise<{ certifications: any[] }> => {
    const response = await api.get('/my-insights/my-certifications');
    return response.data;
  },

  // My Competencies (for My Insights page; same source as dri-insights/competency-trend)
  getMyCompetencies: async (): Promise<{ competencies: CompetencyTrendRecord[] }> => {
    const response = await api.get('/my-insights/my-competencies');
    return response.data;
  },

  // My Competency Progress (card-based competencies tab: cert + DRI milestone breakdown per BU-matched competency)
  getMyCompetencyProgress: async (): Promise<{ competencies: CompetencyProgressCard[]; business_unit: string | null }> => {
    const response = await api.get('/my-insights/my-competency-progress');
    return response.data;
  },

  // Get certifications and skill badges for DRI Insights (filtered by BU/SBU)
  getDriInsightsCertificationsAndSkillBadges: async (filters?: FilterRequest): Promise<{
    certifications: any[];
    skillBadges: any[];
  }> => {
    const response = await api.post('/dri-insights/certifications-skill-badges', filters || {});
    return response.data;
  },

  // Get certification analytics data
  getCertificationAnalytics: async (filters?: FilterRequest): Promise<{
    byServiceProvider: Array<{ provider: string; count: number; percentage: number }>;
    byTypesAndLevels: {
      levels: Array<{ level: string; count: number }>;
      types: Array<{ type: string; count: number; percentage: number }>;
    };
    topCertifications: Array<{ certificationName: string; count: number }>;
    expiringCertifications: Array<{ certificationName: string; count: number }>;
    activeCertifications: Array<{ certificationName: string; count: number }>;
  }> => {
    const response = await api.post('/dri-insights/certification-analytics', filters || {});
    return response.data;
  },

  // Get unregistered employees (employees without DRI details)
  getUnregisteredEmployees: async (filters?: FilterRequest): Promise<any[]> => {
    const response = await api.post('/dri-insights/unregistered-employees', filters || {});
    return response.data;
  },

  // Get weekly DRI trends from materialized table
  getDriWeeklyTrends: async (filters?: FilterRequest): Promise<{
    weekly_trends: any[];
    available_products: string[];
  }> => {
    const response = await api.post('/dri-insights/weekly-trends', filters || {});
    return response.data;
  },

  // Get tier 1 DRI profiles trend (list of profiles with tier1_accomplished_date for month-wise counts)
  getDriInsightsTier1Trend: async (filters?: FilterRequest): Promise<Tier1TrendRecord[]> => {
    const response = await api.post('/dri-insights/tier1-trend', filters || {});
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray((data as any).data)) return (data as any).data;
    if (data && typeof data === 'object' && Array.isArray((data as any).tier1_profiles)) return (data as any).tier1_profiles;
    return [];
  },

  // Get new competencies achieved trend (list with competency_achieved_date for month-wise counts)
  getDriInsightsCompetencyTrend: async (filters?: FilterRequest): Promise<CompetencyTrendRecord[]> => {
    const response = await api.post('/dri-insights/competency-trend', filters || {});
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray((data as any).data)) return (data as any).data;
    if (data && typeof data === 'object' && Array.isArray((data as any).competencies)) return (data as any).competencies;
    return [];
  },

  // Team Insights: tier 1 trend (same schema as DRI, different endpoint)
  getMyTeamTier1Trend: async (filters?: Record<string, unknown>): Promise<Tier1TrendRecord[]> => {
    const response = await api.post('/team-insights/my-team-tier1-trend', filters || {});
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray((data as any).data)) return (data as any).data;
    if (data && typeof data === 'object' && Array.isArray((data as any).tier1_profiles)) return (data as any).tier1_profiles;
    return [];
  },

  // Team Insights: competency trend (same schema as DRI, different endpoint)
  getMyTeamCompetencyTrend: async (filters?: Record<string, unknown>): Promise<CompetencyTrendRecord[]> => {
    const response = await api.post('/team-insights/my-team-competency-trend', filters || {});
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray((data as any).data)) return (data as any).data;
    if (data && typeof data === 'object' && Array.isArray((data as any).competencies)) return (data as any).competencies;
    return [];
  },

};

// User Feedback API (optionally include reference images via FormData)
export const submitUserFeedback = async (
  data: UserFeedbackRequest,
  referenceFiles?: File[]
): Promise<UserFeedbackResponse> => {
  const hasFiles = referenceFiles && referenceFiles.length > 0;

  if (hasFiles) {
    const formData = new FormData();
    formData.append('user_email', data.user_email);
    formData.append('user_name', data.user_name);
    formData.append('category', data.category);
    formData.append('subject', data.subject);
    formData.append('feedback', data.feedback);
    referenceFiles!.forEach((file) => formData.append('references', file));

    const response: AxiosResponse<UserFeedbackResponse> = await api.post(
      '/dashboard/user-feedback',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  const response: AxiosResponse<UserFeedbackResponse> = await api.post(
    '/dashboard/user-feedback',
    data
  );
  return response.data;
};

/** Fetch all user feedback (admin/super_admin). Backend returns { feedbacks, total_count }. */
export const getAllUserFeedback = async (): Promise<UserFeedbackItem[]> => {
  const response: AxiosResponse<GetAllUserFeedbackResponse | UserFeedbackItem[]> = await api.get(
    '/dashboard/user-feedback'
  );
  const data = response.data;
  if (Array.isArray(data)) return data;
  return (data as GetAllUserFeedbackResponse).feedbacks ?? [];
};

/** Mark a feedback item as resolved (admin/super_admin). */
export const resolveUserFeedback = async (id: string): Promise<ResolveUserFeedbackResponse> => {
  const response: AxiosResponse<ResolveUserFeedbackResponse> = await api.patch(
    `/dashboard/user-feedback/${encodeURIComponent(id)}/resolve`,
    {}
  );
  return response.data;
};

// My Files API
export const getMyFiles = async (filters: MyFilesFilterRequest): Promise<MyFilesResponse> => {
  const response: AxiosResponse<MyFilesResponse> = await api.post(
    '/dashboard/my-files',
    filters
  );
  return response.data;
};

// ==================== CUSTOMER REQUEST MODULE (NORMALIZED SCHEMA) ====================

// Enums and Types
export type CustomerRequestType = 'feedback' | 'escalation' | 'appreciation';
export type CustomerRequestLevel = 'project' | 'individual';
export type CustomerRequestStatus = 'draft' | 'pending' | 'email_sent' | 'email_failed' | 'responded' | 'closed';
export type FeedbackType = 'Positive' | 'Negative' | 'Neutral';
export type FeedbackCategory = 'Service Quality' | 'Communication' | 'Technical Expertise' | 'Responsiveness';

// ==================== PROJECT DROPDOWN ====================

export interface ProjectDropdownOption {
  project_code: string;
  project_name: string | null;
  owner: string | null;
  /** Project owner email from tracker (same row as owner name) */
  project_owner_email?: string | null;
  account: string | null;
  delivery_bu: string | null;
  delivery_sub_bu: string | null;
  opportunity_name: string | null;
  workload?: string | null; // For Solution Story form
  status?: string | null;
  closure_date?: string | null;
  project_account_bu?: string | null;
  project_opportunity_domain?: string | null;
  project_opportunity_stage?: string | null;
  solution_story_status?: string | null;
  display_label: string;
}

export interface ProjectDropdownResponse {
  projects: ProjectDropdownOption[];
  total_count: number;
}

export interface TeamMemberOption {
  email: string;
  name: string;
}

export interface TeamMemberResponse {
  team_members: TeamMemberOption[];
  total_count: number;
}

// ==================== CREATE INTERFACES ====================

// Base fields shared across all request types
interface CustomerRequestBaseCreate {
  business_unit?: string;
  project_code?: string;
  project_name?: string;
  account_name?: string;
  project_account_bu?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  project_owner_email?: string;
}

// Feedback Request Create
export interface FeedbackRequestCreate extends CustomerRequestBaseCreate {
  request_type: 'feedback';
  to_emails: string; // Required
  project_title?: string;
  customer_contact_name?: string;
  cc_emails?: string;
  closure_report_url?: string;
}

// Escalation Request Create
export interface EscalationRequestCreate extends CustomerRequestBaseCreate {
  request_type: 'escalation';
  level: CustomerRequestLevel; // Required
  issue_description: string; // Required
  date_raised: string; // Required (ISO date string)
  project_member_name?: string; // Required when level='individual'
  escalated_by_name?: string;
  escalated_by_designation?: string;
  project_owner?: string;
  area_of_escalation?: string;
  date_resolved?: string;
  resolution_status?: string;
  solution_proposed?: string;
  root_cause?: string;
  learnings?: string;
}

// Appreciation Request Create
export interface AppreciationRequestCreate extends CustomerRequestBaseCreate {
  request_type: 'appreciation';
  level: CustomerRequestLevel; // Required
  feedback_snippet: string; // Required
  project_member_name?: string; // Required when level='individual'
  customer_spoc_name?: string;
  customer_spoc_designation?: string;
  feedback_file_url?: string; // GCS URL of uploaded feedback file
}

// Solution Story Request Create
export interface SolutionStoryRequestCreate extends CustomerRequestBaseCreate {
  request_type: 'solution-story';
  solution_provided: string; // Required, max 50 characters
  key_objectives_challenges: string; // Required, min 400 (no max)
  executive_summary: string; // Required, max 150 characters
  challenges_changed_since_inception: string; // Required, min 250 (no max)
  solution_description: string; // Required, min 400 (no max)
  practice: string; // Required: maps to slide practice icon
  platform?: string; // Platform: GCP, Google Maps, AWS, Google Workspace
  platform_usage_way: string; // Required, min 100 (no max)
  business_context: string[]; // Required, array of 4 strings, min 3 filled, total max 400 chars
  solution: string[]; // Required, array of 4 strings, min 3 filled, total max 400 chars
  impact: string[]; // Required, array of 4 strings, min 3 filled, total max 400 chars
  detailed_solution_impact: string; // Required, min 400 (no max)
  tech_stack?: string[]; // Max 6 items
  tech_usage_way: string; // Required, min 100 (no max)
  referencable: 'R' | 'NR'; // Required: R = referenceable, NR = non-referencable
  industry_group?: string; // Industry Group dropdown
  industry?: string; // Industry text field
  teamwork_collaboration?: string; // Optional: Teamwork & Collaboration
  results_business_impact?: string; // Optional: Results & Business Impact
  workload?: string;
  file_urls?: string[]; // GCS file URLs for uploaded files
}

/** Draft save — all fields optional except request_type; no min-length enforcement server-side */
export interface SolutionStoryDraftUpsert {
  request_type: 'solution-story';
  id?: string;
  business_unit?: string;
  project_code?: string;
  project_name?: string;
  account_name?: string;
  opportunity?: string;
  project_owner?: string;
  project_owner_email?: string;
  sub_bu?: string;
  workload?: string;
  solution_provided?: string;
  key_objectives_challenges?: string;
  executive_summary?: string;
  challenges_changed_since_inception?: string;
  solution_description?: string;
  practice?: string;
  platform?: string;
  platform_usage_way?: string;
  business_context?: string[];
  solution?: string[];
  impact?: string[];
  detailed_solution_impact?: string;
  tech_stack?: string[];
  tech_usage_way?: string;
  referencable?: string;
  industry_group?: string;
  industry?: string;
  teamwork_collaboration?: string;
  results_business_impact?: string;
  file_urls?: string[];
}

/** Submit body: same as create plus optional draft to finalize */
export type SolutionStorySubmitBody = SolutionStoryRequestCreate & { draft_id?: string };

export interface PublicTestimonialRequestCreate extends CustomerRequestBaseCreate {
  request_type: 'public-testimonial';
  customer_poc_name: string;
  customer_poc_designation: string;
  date_testimony_received?: string;
  public_testimonial_link: string;
  file_url?: string;
}

// Union type for creating any request
export type CustomerRequestCreate = FeedbackRequestCreate | EscalationRequestCreate | AppreciationRequestCreate | SolutionStoryRequestCreate | PublicTestimonialRequestCreate;

// ==================== UPDATE INTERFACES ====================

export interface FeedbackRequestUpdate {
  business_unit?: string;
  project_code?: string;
  account_name?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  project_title?: string;
  customer_contact_name?: string;
  to_emails?: string;
  cc_emails?: string;
  closure_report_url?: string;
  status?: CustomerRequestStatus;
}

export interface EscalationRequestUpdate {
  business_unit?: string;
  project_code?: string;
  account_name?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  level?: CustomerRequestLevel;
  project_member_name?: string;
  escalated_by_name?: string;
  escalated_by_designation?: string;
  issue_description?: string;
  area_of_escalation?: string;
  date_raised?: string;
  date_resolved?: string;
  resolution_status?: string;
  solution_proposed?: string;
  root_cause?: string;
  learnings?: string;
  status?: CustomerRequestStatus;
}

export interface AppreciationRequestUpdate {
  business_unit?: string;
  project_code?: string;
  account_name?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  level?: CustomerRequestLevel;
  project_member_name?: string;
  customer_spoc_name?: string;
  customer_spoc_designation?: string;
  feedback_snippet?: string;
  status?: CustomerRequestStatus;
}

// ==================== DETAIL RESPONSE INTERFACES ====================

export interface FeedbackDetailResponse {
  project_title?: string;
  customer_contact_name?: string;
  to_emails?: string;
  cc_emails?: string;
  closure_report_url?: string;
  feedback_form_link?: string;  // Customer form link sent via email
  email_sent_at?: string;
  email_error?: string;
  generated_at?: string;
  expires_at?: string;
  response_count?: number;
  last_response_at?: string;
}

export interface EscalationDetailResponse {
  level: CustomerRequestLevel;
  project_member_name?: string;
  escalated_by_name?: string;
  escalated_by_designation?: string;
  issue_description: string;
  area_of_escalation?: string;
  date_raised: string;
  date_resolved?: string;
  resolution_status?: string;
  solution_proposed?: string;
  root_cause?: string;
  learnings?: string;
  // Zoho Integration
  zoho_issue_id?: string;
  zoho_status?: string;
  zoho_issue_link?: string;
}

export interface AppreciationDetailResponse {
  level: CustomerRequestLevel;
  project_member_name?: string;
  customer_spoc_name?: string;
  customer_spoc_designation?: string;
  feedback_snippet: string;
  feedback_file_url?: string;
}

// Customer's feedback response data (submitted via external form)
export interface FeedbackResponseData {
  id: string;
  customer_name?: string;
  customer_email?: string;
  account_name?: string;
  // Overall Satisfaction
  overall_satisfaction?: string; // Highly Satisfied, Satisfied, Neutral, Dissatisfied, Highly Dissatisfied
  // Delivery Quality Ratings
  delivery_completion_of_deliverables?: string;
  delivery_adherence_to_timeline?: string;
  delivery_project_closure_handover?: string;
  // Team Quality Ratings
  team_technical_expertise?: string;
  team_support_provided?: string;
  team_responsiveness?: string;
  // Open-ended Feedback
  specific_contributions?: string;
  areas_for_improvement?: string;
  // Zoho Integration
  zoho_issue_id?: string;
  zoho_status?: string;
  zoho_issue_link?: string;
  // Legacy fields (for backward compatibility)
  feedback_type?: FeedbackType;
  category?: FeedbackCategory;
  rating?: number;
  feedback_details?: string;
  submitted_at?: string;
}

// ==================== MAIN RESPONSE INTERFACE ====================

export interface CustomerRequestResponse {
  id: string;
  request_number: string;
  request_type: CustomerRequestType;
  status: CustomerRequestStatus;
  submitter_email: string;
  submitter_name: string;
  // Project Context (shared)
  business_unit?: string;
  project_code?: string;
  project_name?: string;
  account_name?: string;
  account?: string;
  accounts?: string | string[];
  project_account_bu?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  // Zoho Integration
  zoho_issue_id?: string;
  zoho_status?: string; // N/A, Failed Issue Creation, Open, In Progress, Closed, To be tested
  zoho_issue_link?: string; // URL link to Zoho issue/ticket
  // Closure Date (from project_tracker table)
  closure_date?: string; // Project closure date (actual_end_date from project_tracker)
  // Timestamps
  created_at: string;
  updated_at: string;
  // Type-specific details (only one will be populated based on request_type)
  feedback_detail?: FeedbackDetailResponse;
  escalation_detail?: EscalationDetailResponse;
  appreciation_detail?: AppreciationDetailResponse;
  // Customer feedback responses (only for feedback type, if submitted)
  feedback_responses?: FeedbackResponseData[];
}

// Solution Story List Item (minimal fields for table display)
export interface SolutionStoryListItem {
  id: string;
  request_number?: string; // Format: SOL-YYYY-NNN
  status: string;
  /** Who submitted the form (logged-in user email) */
  submitter_email?: string;
  /** stories.created_at (ISO) — when the story row was first created */
  submitted_at?: string;
  project_code?: string;
  /** From project_details (BigQuery) when available; else stored name */
  project_name?: string;
  account_name?: string;
  project_owner?: string;
  opportunity?: string;
  solution_story_link?: string; // Link to the created slide
  /** SMTP notification email: Success or Failed */
  email_status?: string;
}

// Solution Story Response (full details for modal)
export interface SolutionStoryResponse {
  id: string;
  request_number?: string; // Format: SOL-YYYY-NNN
  status: string;
  submitter_email: string;
  submitter_name: string;
  // Project Context
  business_unit?: string;
  project_code?: string;
  project_name?: string;
  account_name?: string;
  project_account_bu?: string;
  sub_bu?: string;
  opportunity?: string;
  project_owner?: string;
  /** Delivery/project owner email from tracker (not submitter) */
  project_owner_email?: string;
  // Timestamps
  created_at: string;
  updated_at: string;
  // Solution Story specific fields (will be populated from API later)
  workload?: string;
  solution_provided?: string;
  key_objectives_challenges?: string;
  executive_summary?: string;
  challenges_changed_since_inception?: string;
  solution_description?: string;
  practice?: string;
  platform?: string;
  platform_usage_way?: string;
  business_context?: string[];
  solution?: string[];
  impact?: string[];
  detailed_solution_impact?: string;
  tech_stack?: string[];
  tech_usage_way?: string;
  referencable?: string;
  industry_group?: string;
  industry?: string;
  teamwork_collaboration?: string;
  results_business_impact?: string;
  file_urls?: string[];
  solution_story_link?: string;
  /** SMTP notification email: Success or Failed */
  email_status?: string;
}

export interface PublicTestimonialResponse {
  id: string;
  request_number: string; // Format: TES-YYYY-NNN
  status: string;
  submitter_email: string;
  submitter_name: string;
  // Project Context
  business_unit?: string;
  project_code?: string;
  project_name?: string;
  account_name?: string;
  project_account_bu?: string;
  opportunity?: string;
  project_owner?: string;
  sub_bu?: string;
  // Public Testimonial Details
  customer_poc_name?: string;
  customer_poc_designation?: string;
  date_testimony_received?: string;
  public_testimonial_link?: string;
  file_url?: string;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface CustomerRequestListResponse {
  requests: CustomerRequestResponse[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface ResendEmailResponse {
  success: boolean;
  message: string;
  new_expires_at?: string;
}

export interface PublicFeedbackFormResponse {
  request_id: string;
  project_code?: string;
  project_title?: string;
  customer_contact_name?: string;
  company_name?: string;
  is_valid: boolean;
  expires_at?: string;
  message?: string;
}

export interface PublicFeedbackSubmit {
  // Customer Information
  customer_email: string;
  customer_name: string;
  account_name: string;
  // Overall Satisfaction
  overall_satisfaction: string;
  // Delivery Quality Ratings
  delivery_completion_of_deliverables: string;
  delivery_adherence_to_timeline: string;
  delivery_project_closure_handover: string;
  // Team Quality Ratings
  team_technical_expertise: string;
  team_support_provided: string;
  team_responsiveness: string;
  // Open-ended Feedback
  specific_contributions?: string;
  areas_for_improvement?: string;
}

export interface PublicFeedbackSubmitResponse {
  success: boolean;
  message: string;
}

// Customer Request API (Normalized Schema)
// File Upload Response
export interface FileUploadResponse {
  success: boolean;
  message: string;
  file_url: string;
  file_path: string;
  file_name: string;
  file_size: number;
  content_type: string;
}

// File Download Response
export interface FileDownloadResponse {
  success: boolean;
  download_url: string;
  expires_in_hours: number;
}

export const customerRequestApi = {
  // Upload a file for a customer request
  // Note: File is uploaded BEFORE request is created, so we use uploader email + timestamp
  // for identification (handled by backend using auth token)
  uploadFile: async (
    file: File,
    formType: 'feedback' | 'escalation' | 'appreciation' | 'solution-story' | 'public-testimonial'
  ): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('form_type', formType);

    const response = await api.post('/customer/customer-requests/upload-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get a signed download URL for a file
  getFileDownloadUrl: async (filePath: string): Promise<FileDownloadResponse> => {
    const response = await api.get('/customer/customer-requests/download-file', {
      params: { file_path: filePath },
    });
    return response.data;
  },

  // View file content securely (for in-portal viewing)
  viewFile: async (filePath: string): Promise<{ blob: Blob; contentType: string }> => {
    try {
      const response = await api.get('/customer/customer-requests/view-file', {
        params: { file_path: filePath },
        responseType: 'blob', // Important: receive as blob for file content
        validateStatus: (status) => status < 500, // Don't throw on 4xx errors, we'll handle them
      });
      
      // Check if response is an error (4xx status codes)
      if (response.status >= 400) {
        // Error responses are returned as blobs when responseType is 'blob'
        // Try to parse as JSON to get error message
        try {
          const text = await (response.data as Blob).text();
          const errorData = JSON.parse(text);
          const error = new Error(errorData.detail || errorData.message || 'Failed to load file');
          (error as any).response = { status: response.status, data: errorData };
          throw error;
        } catch (parseError: any) {
          // If parsing fails, create a generic error
          const error = new Error('Failed to load file. Please try again.');
          (error as any).response = { status: response.status };
          throw error;
        }
      }
      
      // Get content type from response headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // If content type is JSON, it's likely an error response (shouldn't happen with status check above, but just in case)
      if (contentType.includes('application/json')) {
        try {
          const text = await (response.data as Blob).text();
          const errorData = JSON.parse(text);
          const error = new Error(errorData.detail || errorData.message || 'Failed to load file');
          (error as any).response = { status: response.status, data: errorData };
          throw error;
        } catch (parseError: any) {
          throw new Error('Failed to load file. Invalid response format.');
        }
      }
      
      return {
        blob: response.data,
        contentType,
      };
    } catch (error: any) {
      // Re-throw axios errors with proper structure
      if (error.response) {
        throw error;
      }
      // If it's not an axios error, wrap it
      const wrappedError = new Error(error.message || 'Failed to load file');
      (wrappedError as any).response = error.response || { status: 500 };
      throw wrappedError;
    }
  },

  // Get user's projects for dropdown (projects where user is owner)
  // Optional status filter (e.g., 'Closed' for Feedback form)
  getUserProjects: async (status?: string): Promise<ProjectDropdownResponse> => {
    const params = status ? { status } : {};
    const response = await api.get('/customer/customer-requests/user-projects', { params });
    return response.data;
  },

  // Get team members for a project (for Project Member Name multi-select)
  getProjectTeamMembers: async (projectCode: string): Promise<TeamMemberResponse> => {
    const response = await api.get(`/customer/customer-requests/project-team-members/${encodeURIComponent(projectCode)}`);
    return response.data;
  },

  // Create a Feedback Request
  createFeedback: async (data: FeedbackRequestCreate): Promise<CustomerRequestResponse> => {
    const response = await api.post('/customer/customer-requests/feedback', data);
    return response.data;
  },

  // Create an Escalation Request
  createEscalation: async (data: EscalationRequestCreate): Promise<CustomerRequestResponse> => {
    const response = await api.post('/customer/customer-requests/escalation', data);
    return response.data;
  },

  // Create an Appreciation Request
  createAppreciation: async (data: AppreciationRequestCreate): Promise<CustomerRequestResponse> => {
    const response = await api.post('/customer/customer-requests/appreciation', data);
    return response.data;
  },

  // Create / submit a Solution Story (optional draft_id)
  createSolutionStory: async (data: SolutionStorySubmitBody): Promise<SolutionStoryResponse> => {
    const response = await api.post('/customer/customer-requests/solution-story', data);
    return response.data;
  },

  // Save solution story as draft (no slides)
  saveSolutionStoryDraft: async (data: SolutionStoryDraftUpsert): Promise<SolutionStoryResponse> => {
    const response = await api.post('/customer/customer-requests/solution-story/draft', data);
    return response.data;
  },

  // List Solution Story requests
  listSolutionStories: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<{ requests: SolutionStoryListItem[]; total_count: number; page: number; page_size: number }> => {
    const response = await api.get('/customer/customer-requests/solution-story', { params });
    return response.data;
  },

  // Get Solution Story by ID
  getSolutionStoryById: async (storyId: string): Promise<SolutionStoryResponse> => {
    const response = await api.get(`/customer/customer-requests/solution-story/${storyId}`);
    return response.data;
  },

  /** Resend solution story notification email (same slide URL) */
  retrySolutionStoryEmail: async (storyId: string): Promise<SolutionStoryResponse> => {
    const response = await api.post(`/customer/customer-requests/solution-story/${storyId}/retry-email`);
    return response.data;
  },

  // Create a Public Testimonial Request
  createPublicTestimonial: async (data: PublicTestimonialRequestCreate): Promise<PublicTestimonialResponse> => {
    const response = await api.post('/customer/customer-requests/public-testimonial', data);
    return response.data;
  },

  // List Public Testimonial requests
  listPublicTestimonials: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<{ requests: PublicTestimonialResponse[]; total_count: number; page: number; page_size: number }> => {
    const response = await api.get('/customer/customer-requests/public-testimonial', { params });
    return response.data;
  },

  // List customer requests (all types)
  list: async (params?: {
    request_type?: CustomerRequestType;
    status?: CustomerRequestStatus;
    page?: number;
    page_size?: number;
  }): Promise<CustomerRequestListResponse> => {
    const response = await api.get('/customer/customer-requests', { params });
    return response.data;
  },

  // Get a single customer request
  get: async (requestId: string): Promise<CustomerRequestResponse> => {
    const response = await api.get(`/customer/customer-requests/${requestId}`);
    return response.data;
  },

  // Update a Feedback Request
  updateFeedback: async (requestId: string, data: FeedbackRequestUpdate): Promise<CustomerRequestResponse> => {
    const response = await api.put(`/customer/customer-requests/feedback/${requestId}`, data);
    return response.data;
  },

  // Update an Escalation Request
  updateEscalation: async (requestId: string, data: EscalationRequestUpdate): Promise<CustomerRequestResponse> => {
    const response = await api.put(`/customer/customer-requests/escalation/${requestId}`, data);
    return response.data;
  },

  // Update an Appreciation Request
  updateAppreciation: async (requestId: string, data: AppreciationRequestUpdate): Promise<CustomerRequestResponse> => {
    const response = await api.put(`/customer/customer-requests/appreciation/${requestId}`, data);
    return response.data;
  },

  // Delete a customer request (any type)
  delete: async (requestId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/customer/customer-requests/${requestId}`);
    return response.data;
  },

  // Resend feedback email (only for feedback requests)
  resendEmail: async (requestId: string): Promise<ResendEmailResponse> => {
    const response = await api.post(`/customer/customer-requests/${requestId}/resend-email`);
    return response.data;
  },

  /** Retry Zoho issue creation after Failed Issue Creation */
  retryZoho: async (
    requestId: string,
    body: { scope: 'escalation' | 'feedback_response' | 'feedback_failed_all'; feedback_response_id?: string }
  ): Promise<CustomerRequestResponse> => {
    const response = await api.post(`/customer/customer-requests/${requestId}/retry-zoho`, body);
    return response.data;
  },
};

// Public Feedback API (no authentication required)
export const publicFeedbackApi = {
  // Get feedback form data (validate token)
  getForm: async (token: string): Promise<PublicFeedbackFormResponse> => {
    const response = await api.get(`/public/feedback/${token}`);
    return response.data;
  },

  // Submit feedback
  submit: async (token: string, data: PublicFeedbackSubmit): Promise<PublicFeedbackSubmitResponse> => {
    const response = await api.post(`/public/feedback/${token}`, data);
    return response.data;
  },

  // Check token status
  checkStatus: async (token: string): Promise<{
    valid: boolean;
    status: string;
    message: string;
    expires_at?: string;
  }> => {
    const response = await api.get(`/public/feedback/${token}/status`);
    return response.data;
  },
};

// ==================== SEARCE ASSETS MODULE ====================

export type {
  AssetAiChatHistoryMessage,
  AssetAiQueryRequest,
  AssetAiQueryResponse,
  AssetCreateRequest,
  AssetListResponse,
  AssetResponse,
  InternalDocumentLink,
} from '../types/assets';
export { ASSET_TYPE_OPTIONS, GTM_CATEGORY_OPTIONS, DOCUMENT_CATEGORY_OPTIONS, CLOUD_SERVICE_OPTIONS } from '../types/assets';

export const billingApi = {
  getSandboxBudgets: async (): Promise<BillingBudgetListResponse> => {
    const response = await api.get<BillingBudgetListResponse>('/billing/sandbox-budgets');
    return response.data;
  },

  updateSandboxBudget: async (
    accountId: string,
    data: Partial<BillingBudget>,
  ): Promise<BillingBudget> => {
    const response = await api.put<BillingBudget>(
      `/billing/sandbox-budgets/${encodeURIComponent(accountId)}`,
      data,
    );
    return response.data;
  },

  upsertGcpBudget: async (
    data: GcpBudgetUpsertRequest,
  ): Promise<GcpBudgetUpsertResponse> => {
    const response = await api.post<GcpBudgetUpsertResponse>(
      '/billing/gcp-budgets/upsert',
      data,
    );
    return response.data;
  },

  createSandboxBudget: async (
    data: BillingBudgetCreate,
  ): Promise<BillingBudget> => {
    const response = await api.post<BillingBudget>('/billing/sandbox-budgets', data);
    return response.data;
  },

  bulkDeleteSandboxBudgets: async (
    accountIds: string[],
  ): Promise<{ deleted_count: number }> => {
    const response = await api.post<{ deleted_count: number }>(
      '/billing/sandbox-budgets/bulk-delete',
      { account_ids: accountIds },
    );
    return response.data;
  },

  /** Org-wide Business Unit / Sub-BU master list, for constraining business_unit/sub_bu to a fixed list. */
  getBusinessUnitOptions: async (): Promise<BillingBusinessUnitOption[]> => {
    const response = await api.get<BillingBusinessUnitOption[]>('/admin/business-units');
    return response.data;
  },
};

export const assetsApi = {
  /**
   * Fetch assets. Pass scope 'my_sbu' for assets whose team members include someone
   * in the same Sub-BU as the user (happier data, server-side filter).
   */
  listAssets: async (scope?: 'my_sbu'): Promise<AssetListResponse> => {
    const response = await api.get<AssetListResponse>('/assets', {
      params: scope ? { scope } : undefined,
    });
    return response.data;
  },

  /** Submit a new asset */
  createAsset: async (data: AssetCreateRequest): Promise<AssetResponse> => {
    const response = await api.post('/assets', data);
    return response.data;
  },

  /** Upload a documentation file and return its public URL */
  uploadDocument: async (file: File): Promise<{ file_url: string; file_name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/assets/upload-doc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Verify a documentation URL is accessible via the Drive service account
   * (or HTTP reachability for non-Drive URLs) before adding it to a submission.
   */
  checkDocumentAccess: async (
    url: string,
  ): Promise<{ accessible: boolean; detail?: string | null }> => {
    const response = await api.post<{ accessible: boolean; detail?: string | null }>(
      '/assets/check-doc-access',
      { url },
    );
    return response.data;
  },

  /** Get the current user's team (peers sharing the same manager) */
  getUserTeam: async (): Promise<{ manager_email: string | null; team_members: { email: string; name: string }[] }> => {
    const response = await api.get('/assets/user-team');
    return response.data;
  },

  /**
   * Ask the AI microservice (REACT_APP_AI_CHATBOT_URL) POST /query.
   * Sends Bearer JWT from localStorage, same as the dashboard api client.
   */
  aiQuery: async (request: AssetAiQueryRequest): Promise<AssetAiQueryResponse> => {
    const base = AI_BASE_URL.replace(/\/$/, '');
    const token = localStorage.getItem('access_token');
    const file = request.file ?? null;

    if (file) {
      if (file.size > ASSET_AI_QUERY_UPLOAD_MAX_BYTES) {
        throw new Error(
          `File is too large (max ${Math.round(ASSET_AI_QUERY_UPLOAD_MAX_BYTES / (1024 * 1024))} MB).`
        );
      }
      const payload = buildAssetAiQueryPayload(request);
      const fd = new FormData();
      fd.append('user_question', payload.user_question);
      if (payload.chat_history && payload.chat_history.length > 0) {
        fd.append('chat_history', JSON.stringify(payload.chat_history));
      }
      fd.append('file', file, file.name);
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await axios.post<AssetAiQueryResponse>(`${base}/query`, fd, {
        headers,
        timeout: 120000,
        maxContentLength: ASSET_AI_QUERY_UPLOAD_MAX_BYTES + 1024 * 1024,
        maxBodyLength: ASSET_AI_QUERY_UPLOAD_MAX_BYTES + 1024 * 1024,
      });
      return response.data;
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const data = normalizeAssetAiQueryBody(request);
    const response = await axios.post<AssetAiQueryResponse>(`${base}/query`, data, {
      headers,
      timeout: 120000,
    });
    return response.data;
  },
};

/** User email autocomplete (debounced client should call with query length ≥ 2) */
export interface UserAutocomplete {
  id: string;
  email: string;
  name: string;
}

export const usersService = {
  autocompleteUsers: async (query: string, limit = 10): Promise<UserAutocomplete[]> => {
    const response = await api.get<UserAutocomplete[]>('/user/autocomplete', {
      params: { query, limit },
    });
    return response.data;
  },
};

// HR Statistics drill-down page (`/hr-stats`).
// All endpoints are POST-only because filters are JSON keyed by dimension.
export const hrStatsApi = {
  getBreakdown: async (
    request: HrStatsBreakdownRequest,
  ): Promise<HrStatsBreakdownResponse> => {
    const response = await api.post<HrStatsBreakdownResponse>(
      '/hr-stats/breakdown',
      request,
    );
    return response.data;
  },

  getSummary: async (request: HrStatsBaseRequest): Promise<HrStatsSummaryResponse> => {
    const response = await api.post<HrStatsSummaryResponse>('/hr-stats/summary', request);
    return response.data;
  },

  getDimensionValues: async (
    request: HrStatsDimensionValuesRequest,
  ): Promise<HrStatsDimensionValuesResponse> => {
    const response = await api.post<HrStatsDimensionValuesResponse>(
      '/hr-stats/dimension-values',
      request,
    );
    return response.data;
  },

  getPeople: async (
    request: HrStatsPeopleRequest,
  ): Promise<HrStatsPeopleResponse> => {
    const response = await api.post<HrStatsPeopleResponse>(
      '/hr-stats/people',
      request,
    );
    return response.data;
  },
};

export { api };
export default api;

export async function getTableSyncStatus(): Promise<Record<string, string | null>> {
  const response = await api.get<Record<string, string | null>>('/dashboard/sync-status');
  return response.data;
}

// ============================================================================
// Solution Stories API
// ============================================================================
export interface SolutionStory {
  solution_story_id: string;
  solution_provided_title: string;
  business_context_1?: string | null;
  business_context_2?: string | null;
  business_context_3?: string | null;
  business_context_4?: string | null;
  solution_1?: string | null;
  solution_2?: string | null;
  solution_3?: string | null;
  solution_4?: string | null;
  impact_1?: string | null;
  impact_2?: string | null;
  impact_3?: string | null;
  impact_4?: string | null;
  technology?: string | null;
  platform?: string | null;
  tech_stack?: string | null;
  industry?: string | null;
  region?: string | null;
  referencable?: string | null;
  company_details?: string | null;
  practice?: string | null;
  pr_code?: string | null;
  opportunity_id?: string | null;
  opportunity_status?: string | null;
  project_status?: string | null;
  account_name?: string | null;
  review_status?: string | null;
  reviewer?: string | null;
  created_time?: string | null;
  updated_time?: string | null;
  /** From the customer-submitted `stories` table, matched by pr_code; null when no match. */
  tech_usage_way?: string | null;
  platform_usage_way?: string | null;
  key_objectives_challenges?: string | null;
  challenges_changed_since_inception?: string | null;
  /** From sfdc.deal_win_stories, matched by opportunity_id; null when no match. */
  win_strategy?: string | null;
  winning_strategy_heading?: string | null;
  winning_strategy_subheading?: string | null;
  win_wire_story?: string | null;
  /** True for a standalone deal-win card with no matching solution story. */
  is_deal_win_only?: boolean;
}

export interface SolutionStoryListResponse {
  stories: SolutionStory[];
  total_count: number;
}

export interface SolutionStoryFilterOptions {
  regions: string[];
  industries: string[];
  platforms: string[];
  tech_stacks: string[];
  opportunity_statuses: string[];
  practices: string[];
  referencables: string[];
}

export interface SolutionStoryFilters {
  region?: string[];
  industry?: string[];
  platform?: string[];
  tech_stack?: string[];
  opportunity_status?: string[];
  practice?: string[];
  referencable?: string[];
  search?: string;
}

export interface GenerateSlidesResponse {
  presentation_id: string;
  presentation_url: string;
  file_name: string;
  slide_count: number;
  story_ids: string[];
}

/** Reviewer-editable fields for admin PATCH. */
export interface SolutionStoryAdminUpdate {
  solution_provided_title?: string | null;
  account_name?: string | null;
  company_details?: string | null;
  business_context_1?: string | null;
  business_context_2?: string | null;
  business_context_3?: string | null;
  business_context_4?: string | null;
  solution_1?: string | null;
  solution_2?: string | null;
  solution_3?: string | null;
  solution_4?: string | null;
  impact_1?: string | null;
  impact_2?: string | null;
  impact_3?: string | null;
  impact_4?: string | null;
  platform?: string | null;
  tech_stack?: string | null;
  industry?: string | null;
  region?: string | null;
  /** Deal-win-only card fields (sfdc.deal_win_stories); ignored for regular solution stories. */
  practice?: string | null;
  winning_strategy_subheading?: string | null;
  win_strategy?: string | null;
  win_wire_story?: string | null;
}

export interface SolutionStoryAdminAccessResponse {
  allowed: boolean;
}

export interface SolutionStoryDirectApprovalPrLookup {
  pr_code: string;
  opportunity_status: string;
  project_status: string;
  next_solution_story_id: string;
  account_name?: string | null;
  region?: string | null;
}

export interface SolutionStoryDirectApprovalCreate {
  pr_code: string;
  solution_provided_title: string;
  company_details?: string | null;
  business_context_1?: string | null;
  business_context_2?: string | null;
  business_context_3?: string | null;
  business_context_4?: string | null;
  solution_1?: string | null;
  solution_2?: string | null;
  solution_3?: string | null;
  solution_4?: string | null;
  impact_1?: string | null;
  impact_2?: string | null;
  impact_3?: string | null;
  impact_4?: string | null;
  platform?: string | null;
  tech_stack?: string | null;
  industry?: string | null;
  region?: string | null;
  referencable?: string | null;
  practice?: string | null;
  account_name?: string | null;
  create_slides_and_email?: boolean;
}

export interface SolutionStoryDirectApprovalResponse {
  story: SolutionStory;
  presentation_id?: string | null;
  presentation_url?: string | null;
  file_name?: string | null;
  slide_count?: number | null;
}


export type SolutionStoryOptionCategory = 'practice' | 'industry' | 'platform' | 'techstack' | 'company';

export interface SolutionStoryOption {
  id: string;
  category: SolutionStoryOptionCategory;
  value: string;
  label?: string | null;
  icon_url?: string | null;
  icon_path?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface SolutionStoryIconMappings {
  industry: Record<string, string>;
  industry_labels: Record<string, string>;
  practice: Record<string, string>;
  platform: Record<string, string>;
  techstack: Record<string, string>;
  company: Record<string, string>;
}

export interface SolutionStoryOptionsGrouped {
  practice: SolutionStoryOption[];
  industry: SolutionStoryOption[];
  platform: SolutionStoryOption[];
  techstack: SolutionStoryOption[];
  company: SolutionStoryOption[];
  icon_mappings: SolutionStoryIconMappings;
}

export interface SolutionStoryOptionCreate {
  category: SolutionStoryOptionCategory;
  value: string;
  label?: string | null;
  sort_order?: number | null;
}

export interface SolutionStoryOptionUpdate {
  value?: string | null;
  label?: string | null;
  sort_order?: number | null;
  is_active?: boolean | null;
}

export const solutionStoriesApi = {
  list: async (filters: SolutionStoryFilters = {}): Promise<SolutionStoryListResponse> => {
    const params = new URLSearchParams();
    if (filters.search?.trim()) params.append('search', filters.search.trim());
    (filters.region || []).forEach(v => params.append('region', v));
    (filters.industry || []).forEach(v => params.append('industry', v));
    (filters.platform || []).forEach(v => params.append('platform', v));
    (filters.tech_stack || []).forEach(v => params.append('tech_stack', v));
    (filters.opportunity_status || []).forEach(v => params.append('opportunity_status', v));
    (filters.practice || []).forEach(v => params.append('practice', v));
    (filters.referencable || []).forEach(v => params.append('referencable', v));
    const response = await api.get<SolutionStoryListResponse>(`/solution-stories?${params.toString()}`);
    return response.data;
  },

  getFilterOptions: async (): Promise<SolutionStoryFilterOptions> => {
    const response = await api.get<SolutionStoryFilterOptions>('/solution-stories/filter-options');
    return response.data;
  },

  generateSlides: async (storyIds: string[]): Promise<GenerateSlidesResponse> => {
    // Deck generation is synchronous and can run several minutes for multi-slide decks
    // (each slide is multiple Slides API writes, plus rate-limit backoff at 60 writes/min/user).
    // Override the default 120s client timeout so the browser waits for the backend to finish
    // instead of aborting a deck that actually gets created.
    const response = await api.post<GenerateSlidesResponse>(
      '/solution-stories/generate-slides',
      { story_ids: storyIds },
      { timeout: 600000 },
    );
    return response.data;
  },

  listAdminPending: async (): Promise<SolutionStoryListResponse> => {
    const response = await api.get<SolutionStoryListResponse>('/solution-stories/admin/pending');
    return response.data;
  },

  listAdminMine: async (): Promise<SolutionStoryListResponse> => {
    const response = await api.get<SolutionStoryListResponse>('/solution-stories/admin/mine');
    return response.data;
  },

  listAdminAll: async (): Promise<SolutionStoryListResponse> => {
    const response = await api.get<SolutionStoryListResponse>('/solution-stories/admin/all');
    return response.data;
  },

  getAdminAccess: async (): Promise<SolutionStoryAdminAccessResponse> => {
    const response = await api.get<SolutionStoryAdminAccessResponse>('/solution-stories/admin/access');
    return response.data;
  },

  listDirectApprovalProjects: async (): Promise<ProjectDropdownResponse> => {
    const response = await api.get<ProjectDropdownResponse>(
      '/solution-stories/admin/direct-approval/projects'
    );
    return response.data;
  },

  updateAdminStory: async (
    storyId: string,
    payload: SolutionStoryAdminUpdate
  ): Promise<SolutionStory> => {
    const response = await api.patch<SolutionStory>(
      `/solution-stories/admin/${encodeURIComponent(storyId)}`,
      payload
    );
    return response.data;
  },

  approveStory: async (
    storyId: string,
    createSlidesAndEmail = false
  ): Promise<GenerateSlidesResponse> => {
    const response = await api.post<GenerateSlidesResponse>(
      `/solution-stories/admin/${encodeURIComponent(storyId)}/approve`,
      { create_slides_and_email: createSlidesAndEmail }
    );
    return response.data;
  },

  archiveStory: async (storyId: string): Promise<SolutionStory> => {
    const response = await api.post<SolutionStory>(
      `/solution-stories/admin/${encodeURIComponent(storyId)}/archive`
    );
    return response.data;
  },

  lookupDirectApprovalPr: async (prCode: string): Promise<SolutionStoryDirectApprovalPrLookup> => {
    const params = new URLSearchParams();
    params.append('pr_code', prCode.trim());
    const response = await api.get<SolutionStoryDirectApprovalPrLookup>(
      `/solution-stories/admin/direct-approval/pr-lookup?${params.toString()}`
    );
    return response.data;
  },

  createDirectApprovalStory: async (
    payload: SolutionStoryDirectApprovalCreate
  ): Promise<SolutionStoryDirectApprovalResponse> => {
    const response = await api.post<SolutionStoryDirectApprovalResponse>(
      '/solution-stories/admin/direct-approval',
      payload
    );
    return response.data;
  },

  getOptions: async (): Promise<SolutionStoryOptionsGrouped> => {
    const response = await api.get<SolutionStoryOptionsGrouped>('/solution-stories/options');
    return response.data;
  },

  listAdminOptions: async (
    category?: SolutionStoryOptionCategory,
    activeOnly = false
  ): Promise<SolutionStoryOption[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    params.append('active_only', String(activeOnly));
    const response = await api.get<SolutionStoryOption[]>(
      `/solution-stories/admin/options?${params.toString()}`
    );
    return response.data;
  },

  createOption: async (payload: SolutionStoryOptionCreate): Promise<SolutionStoryOption> => {
    const response = await api.post<SolutionStoryOption>('/solution-stories/admin/options', payload);
    return response.data;
  },

  updateOption: async (
    optionId: string,
    payload: SolutionStoryOptionUpdate
  ): Promise<SolutionStoryOption> => {
    const response = await api.patch<SolutionStoryOption>(
      `/solution-stories/admin/options/${encodeURIComponent(optionId)}`,
      payload
    );
    return response.data;
  },

  deleteOption: async (optionId: string): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(
      `/solution-stories/admin/options/${encodeURIComponent(optionId)}`
    );
    return response.data;
  },

  reorderOptions: async (
    items: { id: string; sort_order: number }[]
  ): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(
      '/solution-stories/admin/options/reorder',
      { items }
    );
    return response.data;
  },

  uploadOptionIcon: async (optionId: string, file: File): Promise<SolutionStoryOption> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<SolutionStoryOption>(
      `/solution-stories/admin/options/${encodeURIComponent(optionId)}/icon`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};
